import React, { useMemo } from 'react';
import Button from '@atlaskit/button/new';
import { xcss, Box } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { RowHeight } from '../../types.tsx';
import { TableCell } from '../table-cell/index.tsx';
import { useCellBorderStyle } from '../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

const ROW_HEIGHT: RowHeight = 40;

export const LoadMoreItemsRow = ({
	loadNext,
	isLoadingNext = false,
	columnsCount,
	isStickyColumnsVisible,
	hasCheckboxColumn,
}: {
	loadNext: () => void;
	isLoadingNext?: boolean;
	columnsCount: number;
	isStickyColumnsVisible?: boolean;
	hasCheckboxColumn?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const cellBorderStyle = useCellBorderStyle();

	const button = (
		<Button appearance="subtle" spacing="compact" isDisabled={isLoadingNext} onClick={loadNext}>
			{isLoadingNext
				? formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.loadingMoreItemsIssueTermRefresh
							: messages.loadingMoreItems,
					)
				: formatMessage(messages.showMore)}
		</Button>
	);

	const cellBorderStyles = useMemo(() => {
		return fg('jira_timeline_cell_border_style')
			? [baseCellBorderStyles, cellBorderLocationStyleMap[cellBorderStyle]]
			: [cellBorderStylesOld];
	}, [cellBorderStyle]);

	const isColumnSticky = isStickyColumnsVisible && fg('issue_table_frozen_columns');
	const stickyButton = (
		<Box
			testId="native-issue-table.common.ui.load-more-items-row.load-more-wrapper"
			xcss={stickyContainerStyles}
		>
			{button}
		</Box>
	);

	return (
		<Box as="tr" xcss={rowStyles}>
			{isColumnSticky && hasCheckboxColumn === false ? (
				<TableCell
					hasRightShadow
					spacing={firstColumnSpacingStyles}
					border={firstColumnBorderStyles}
					isColumnSticky={isColumnSticky}
				>
					{stickyButton}
				</TableCell>
			) : (
				<>
					<TableCell border={cellBorderStyles} isColumnSticky={isColumnSticky} />
					<TableCell border={cellBorderStyles} colSpan={columnsCount}>
						{isColumnSticky ? stickyButton : button}
					</TableCell>
				</>
			)}
		</Box>
	);
};

const rowStyles = xcss({
	height: `${ROW_HEIGHT}px`,
	backgroundColor: 'elevation.surface',
});

// TODO: Remove this with cleanup of jira_timeline_cell_border_style
const cellBorderStylesOld = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderTopWidth: 'border.width',
	borderBottomWidth: '0',
	borderLeftWidth: '0',
	borderRightWidth: '0',
});

const baseCellBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderBottomWidth: '0',
	borderLeftWidth: '0',
	borderRightWidth: '0',
});

const topCellBorderStyles = xcss({
	borderTopWidth: 'border.width',
});

const noRowCellBorderStyles = xcss({
	borderTopWidth: '0',
});

const cellBorderLocationStyleMap = {
	FULL_BORDER: topCellBorderStyles,
	NO_COLUMN_BORDER: topCellBorderStyles,
	ROW_BORDER_FOR_EXPANDED_TOP_LEVEL_ONLY: noRowCellBorderStyles,
};

const stickyContainerStyles = xcss({
	position: 'sticky',
	left: 'space.600',
	width: 'fit-content',
});

const firstColumnSpacingStyles = xcss({
	padding: '0',
});

const firstColumnBorderStyles = xcss({
	borderRightColor: 'color.border',
	borderRightStyle: 'solid',
	borderRightWidth: 'border.width',
});
